<template>
  <b-modal
    id="modal-apply-wfh"
    ref="modal-apply-wfh"
    title=""
    size="lg"
    modal-class="attendance-apply-modal apply-wfh-modal"
    content-class="attendance-apply-modal-content"
    body-class="p-0"
    hide-footer
    hide-header
    centered
    @hidden="$emit('reset')"
  >
    <div class="wfh-request-container">
      <b-row class="no-gutters">
        <!-- Left: Form -->
        <b-col lg="7" md="12" cols="12" class="wfh-form-section order-1">
          <div class="wfh-form-content">
            <div class="modal-flow-header">
              <div class="d-flex align-items-center justify-content-between">
                <div class="d-flex align-items-center">
                  <div class="modal-flow-icon">
                    <feather-icon icon="HomeIcon" size="18" />
                  </div>
                  <div>
                    <h5 class="mb-0 font-weight-bolder">
                      {{ wfhForm.id ? 'Edit WFH' : 'Apply WFH' }}
                    </h5>
                    <small class="text-muted">Work from home request</small>
                  </div>
                </div>
                <b-button variant="flat-secondary" class="btn-icon p-50" @click="hide">
                  <feather-icon icon="XIcon" size="20" />
                </b-button>
              </div>

              <!-- Compact WFH limit strip -->
              <div class="balance-strip mt-2">
                <template v-if="wfhForm.selected_employee">
                  <b-badge
                    v-if="selectedEmployeeIsPermanentWFH"
                    pill
                    variant="success"
                    class="mb-0"
                  >
                    Permanent WFH — no quarterly limit
                  </b-badge>
                  <template v-else>
                    <b-badge
                      pill
                      :class="['wfh-limit-badge', selectedEmployeeWFHCount >= 2 ? 'limit-reached' : '']"
                    >
                      Used {{ selectedEmployeeWFHCount }} / 2 this quarter
                    </b-badge>
                    <small class="text-muted ml-1">{{ getUserName(wfhForm.selected_employee) }}</small>
                  </template>
                </template>
                <template v-else>
                  <b-badge
                    v-if="isPermanentWFH"
                    pill
                    variant="success"
                    class="mb-0"
                  >
                    Permanent WFH — no quarterly limit
                  </b-badge>
                  <b-badge
                    v-else
                    pill
                    :class="['wfh-limit-badge', currentQuarterWFHCount >= 2 ? 'limit-reached' : '']"
                  >
                    Used {{ currentQuarterWFHCount }} / 2 this quarter
                  </b-badge>
                </template>
              </div>
            </div>

            <b-form>
              <b-form-group
                v-if="(viewMode === 'team' || viewMode === 'org') && canManageLeaves && canApplyOnBehalf"
                label="Apply on behalf of"
                label-for="selected-employee-wfh"
                class="mb-2"
              >
                <b-form-select
                  id="selected-employee-wfh"
                  :value="wfhForm.selected_employee"
                  :options="employeeOptions"
                  @change="$emit('employee-change', $event)"
                >
                  <template #first>
                    <option :value="null">Myself (default)</option>
                  </template>
                </b-form-select>
              </b-form-group>

              <b-alert v-if="!wfhForm.selected_employee && isOnProbation" variant="warning" show class="mb-2 py-1 px-2 compact-alert">
                <feather-icon icon="AlertCircleIcon" size="14" class="mr-50" />
                On probation — HR will decide paid/unpaid.
              </b-alert>
              <b-alert v-if="!wfhForm.selected_employee && managerAbsent && !isOnProbation" variant="info" show class="mb-2 py-1 px-2 compact-alert">
                <feather-icon icon="InfoIcon" size="14" class="mr-50" />
                Manager on leave — request routes to HR.
              </b-alert>

              <div class="date-summary-row mb-2">
                <div class="date-summary-cell">
                  <label class="date-summary-label" for="wfh-date">WFH date</label>
                  <b-form-input
                    id="wfh-date"
                    :value="wfhForm.date ? formatDateForDisplay(wfhForm.date) : ''"
                    readonly
                    class="bg-light date-summary-input"
                    placeholder="—"
                  />
                </div>
              </div>
              <small class="text-muted d-block mb-2 date-hint">
                <feather-icon icon="CalendarIcon" size="12" class="mr-25" />
                Pick a date on the calendar
              </small>

              <b-form-group label="Duration" label-for="wfh-duration" class="mb-2">
                <b-form-radio-group
                  id="wfh-duration"
                  :checked="wfhForm.is_half_day"
                  :options="[
                    { value: false, text: 'Full Day' },
                    { value: true, text: 'Half Day' }
                  ]"
                  buttons
                  button-variant="outline-primary"
                  size="sm"
                  @change="$emit('update:is-half-day', $event)"
                />
              </b-form-group>

              <b-form-group
                v-if="wfhForm.is_half_day === true"
                label="Select half"
                label-for="wfh-half-day-type"
                class="mb-2"
              >
                <b-form-radio-group
                  id="wfh-half-day-type"
                  :checked="wfhForm.half_day_type"
                  :options="[
                    { value: '1st Half Day', text: '1st Half' },
                    { value: '2nd Half Day', text: '2nd Half' }
                  ]"
                  buttons
                  button-variant="outline-primary"
                  size="sm"
                  @change="$emit('update:half-day-type', $event)"
                />
              </b-form-group>

              <b-form-group class="mb-2">
                <b-form-checkbox
                  :checked="wfhForm.partial_wfh"
                  @change="$emit('update:partial-wfh', $event)"
                >
                  <span class="font-weight-semibold">Already at office — switching to WFH</span>
                  <small class="d-block text-muted">Morning office attendance still counts</small>
                </b-form-checkbox>
              </b-form-group>

              <b-form-group label="Reason" label-for="wfh-reason" class="mb-2">
                <b-form-textarea
                  id="wfh-reason"
                  :value="wfhForm.comment"
                  rows="2"
                  :placeholder="wfhForm.partial_wfh ? 'E.g. Switching to WFH from 2 PM due to...' : 'Reason for work from home'"
                  required
                  @input="$emit('update:comment', $event)"
                />
              </b-form-group>

              <div class="modal-flow-footer">
                <b-button variant="outline-secondary" size="sm" @click="hide">
                  Cancel
                </b-button>
                <b-button
                  variant="primary"
                  class="btn-attendance-primary"
                  @click="$emit('submit')"
                >
                  <feather-icon icon="CheckIcon" size="14" class="mr-50" />
                  {{ wfhForm.id ? 'Update' : 'Apply WFH' }}
                </b-button>
              </div>
            </b-form>
          </div>
        </b-col>

        <!-- Right: Calendar -->
        <b-col lg="5" md="12" cols="12" class="wfh-calendar-section order-2">
          <div class="wfh-calendar-content">
            <div class="calendar-header mb-2">
              <div class="d-flex justify-content-between align-items-center">
                <b-button variant="flat-primary" class="btn-icon p-25" @click="$emit('navigate-calendar', -1)">
                  <feather-icon icon="ChevronLeftIcon" size="18" />
                </b-button>
                <h6 class="mb-0 font-weight-bolder calendar-month-label">{{ displayCalendarMonthYear }}</h6>
                <b-button variant="flat-primary" class="btn-icon p-25" @click="$emit('navigate-calendar', 1)">
                  <feather-icon icon="ChevronRightIcon" size="18" />
                </b-button>
              </div>
            </div>
            <div class="calendar-wrapper">
              <div class="calendar-grid">
                <div class="calendar-weekdays">
                  <div v-for="(day, idx) in weekDayLabels" :key="`wfh-weekday-${idx}`" class="weekday">{{ day }}</div>
                </div>
                <div class="calendar-days">
                  <div
                    v-for="(day, index) in calendarDays"
                    :key="`wfh-day-${day.dateStr}-${index}`"
                    class="calendar-day-item"
                    :class="getCalendarDayClasses(day)"
                    :title="day.isDisabled ? 'Unavailable' : ''"
                    @click="!day.isDisabled && $emit('select-date', day)"
                  >
                    {{ day.date }}
                  </div>
                </div>
              </div>
            </div>
            <div class="calendar-legend mt-2">
              <span class="legend-item"><span class="legend-swatch available" /> Available</span>
              <span class="legend-item"><span class="legend-swatch unavailable" /> Unavailable</span>
              <span class="legend-item"><span class="legend-swatch selected" /> Selected</span>
            </div>
            <div v-if="wfhForm.date" class="text-center mt-2">
              <b-button variant="outline-secondary" size="sm" @click="$emit('clear-date')">
                <feather-icon icon="XIcon" size="12" class="mr-25" />
                Clear date
              </b-button>
            </div>
          </div>
        </b-col>
      </b-row>
    </div>
  </b-modal>
</template>

<script>
import {
  BModal,
  BRow,
  BCol,
  BForm,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BFormSelect,
  BFormSelectOption,
  BFormCheckbox,
  BFormRadioGroup,
  BAlert,
  BButton,
  BBadge,
} from 'bootstrap-vue'

export default {
  name: 'WFHModal',
  components: {
    BModal,
    BRow,
    BCol,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BFormSelect,
    BFormSelectOption,
    BFormCheckbox,
    BFormRadioGroup,
    BAlert,
    BButton,
    BBadge,
  },
  props: {
    wfhForm: {
      type: Object,
      required: true,
    },
    viewMode: {
      type: String,
      required: true,
    },
    canManageLeaves: {
      type: Boolean,
      default: false,
    },
    canApplyOnBehalf: {
      type: Boolean,
      default: false,
    },
    employeeOptions: {
      type: Array,
      default: () => [],
    },
    isOnProbation: {
      type: Boolean,
      default: false,
    },
    managerAbsent: {
      type: Boolean,
      default: false,
    },
    isPermanentWFH: {
      type: Boolean,
      default: false,
    },
    selectedEmployeeIsPermanentWFH: {
      type: Boolean,
      default: false,
    },
    selectedEmployeeWFHCount: {
      type: Number,
      default: 0,
    },
    currentQuarterWFHCount: {
      type: Number,
      default: 0,
    },
    displayCalendarMonthYear: {
      type: String,
      required: true,
    },
    weekDayLabels: {
      type: Array,
      required: true,
    },
    calendarDays: {
      type: Array,
      required: true,
    },
    getUserName: {
      type: Function,
      required: true,
    },
    formatDateForDisplay: {
      type: Function,
      required: true,
    },
    getCalendarDayClasses: {
      type: Function,
      required: true,
    },
  },
  methods: {
    show() {
      this.$nextTick(() => {
        this.$root.$emit('bv::show::modal', 'modal-apply-wfh')
      })
    },
    hide() {
      this.$nextTick(() => {
        this.$root.$emit('bv::hide::modal', 'modal-apply-wfh')
      })
    },
  },
}
</script>

<style lang="scss" scoped>
$att-teal: #0099AC;
$att-teal-dark: #007a8a;
$att-border: #e5e7eb;
$att-bg-soft: #f3fafb;

.wfh-request-container {
  min-height: 280px;
  max-height: 78vh;
  overflow-y: auto;
}

.wfh-form-section {
  padding: 1rem 1.1rem 0.85rem;
  background: #fff;
  border-right: 1px solid $att-border;
}

.wfh-calendar-section {
  padding: 1rem;
  background: $att-bg-soft;
}

.modal-flow-header {
  margin-bottom: 0.75rem;
  padding-bottom: 0.65rem;
  border-bottom: 1px solid $att-border;
}

.modal-flow-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 0.65rem;
  background: rgba(255, 159, 67, 0.15);
  color: #e08a2b;
}

.balance-strip {
  background: #f8fafb;
  border: 1px solid $att-border;
  border-radius: 8px;
  padding: 0.4rem 0.55rem;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.wfh-limit-badge {
  background: rgba(0, 153, 172, 0.12) !important;
  color: $att-teal-dark !important;
  font-size: 0.72rem;
  font-weight: 600;
  padding: 0.35em 0.7em;
}

.wfh-limit-badge.limit-reached {
  background: rgba(234, 84, 85, 0.12) !important;
  color: #ea5455 !important;
}

.compact-alert {
  font-size: 0.8rem;
  line-height: 1.35;
}

.date-summary-row {
  display: flex;
  gap: 0.5rem;
}

.date-summary-cell {
  flex: 1;
  min-width: 120px;
}

.date-summary-label {
  display: block;
  font-size: 0.72rem;
  font-weight: 600;
  color: #6b7280;
  margin-bottom: 0.2rem;
}

.date-summary-input {
  font-size: 0.85rem;
  min-height: 36px;
}

.date-hint {
  font-size: 0.72rem;
}

.modal-flow-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  margin-top: 1rem;
  padding-top: 0.75rem;
  border-top: 1px solid $att-border;
}

.btn-attendance-primary {
  background: $att-teal !important;
  border-color: $att-teal !important;
  color: #fff !important;
  font-weight: 600;
}

.btn-attendance-primary:hover,
.btn-attendance-primary:focus {
  background: $att-teal-dark !important;
  border-color: $att-teal-dark !important;
}

.calendar-header {
  padding: 0.4rem 0.5rem;
  background: #fff;
  border: 1px solid $att-border;
  border-radius: 8px;
}

.calendar-month-label {
  color: $att-teal-dark;
  font-size: 0.9rem;
}

.calendar-wrapper {
  display: flex;
  justify-content: center;
}

.calendar-grid {
  width: 100%;
  max-width: 260px;
}

.calendar-weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0.2rem;
  margin-bottom: 0.35rem;
}

.weekday {
  text-align: center;
  font-weight: 600;
  font-size: 0.7rem;
  color: #6c757d;
  padding: 0.2rem 0;
}

.calendar-days {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0.2rem;
}

.calendar-day-item {
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.78rem;
  position: relative;
  color: #374151;
  min-height: 30px;
  border: 1px solid transparent;
  background: #fff;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.calendar-day-item:hover:not(.other-month):not(.selected):not(.disabled) {
  background: rgba(0, 153, 172, 0.1);
  border-color: rgba(0, 153, 172, 0.35);
}

.calendar-day-item.selected {
  background: $att-teal;
  border-color: $att-teal;
  color: #fff;
}

.calendar-day-item.other-month {
  opacity: 0.35;
  cursor: default;
}

.calendar-day-item.today:not(.selected):not(.disabled) {
  border-color: $att-teal;
  color: $att-teal-dark;
}

.calendar-day-item.disabled {
  cursor: not-allowed;
  background: #f3f4f6;
  color: #9ca3af;
}

.calendar-day-item.disabled::after {
  content: '';
  position: absolute;
  left: 18%;
  right: 18%;
  top: 50%;
  height: 1.5px;
  background: rgba(156, 163, 175, 0.85);
  transform: rotate(-28deg);
  pointer-events: none;
}

.calendar-day-item.holiday-disabled:not(.selected) {
  background: #fff8e6;
  color: #a16207;
}

.calendar-legend {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.55rem;
  font-size: 0.68rem;
  color: #6b7280;
}

.legend-item {
  display: inline-flex;
  align-items: center;
}

.legend-swatch {
  width: 10px;
  height: 10px;
  border-radius: 3px;
  margin-right: 0.3rem;
  display: inline-block;
  border: 1px solid transparent;
}

.legend-swatch.available {
  background: #fff;
  border-color: #d1d5db;
}

.legend-swatch.unavailable {
  background: #f3f4f6;
  box-shadow: inset 0 0 0 1px #d1d5db;
}

.legend-swatch.selected {
  background: $att-teal;
}

@media (max-width: 991.98px) {
  .wfh-form-section {
    border-right: none;
    border-bottom: 1px solid $att-border;
  }
}
</style>

<!-- b-modal teleports to body; unscoped so frame radius applies -->
<style lang="scss">
.attendance-apply-modal.apply-wfh-modal .modal-content,
.attendance-apply-modal.apply-wfh-modal .attendance-apply-modal-content {
  border-radius: 0.75rem;
  overflow: hidden;
}
</style>
